import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "../css/app.css";
import "../css/styles.css";
import "../css/markdown.css";
import "../css/searchsuggest.css";
import {
  Avatar,
  Badge,
  styled,
  Box,
  Typography,
  Stack,
  Button,
  Tooltip,
} from "@mui/material";
import avatarImg1 from "../assets/linklogo.jpg";
import TextField from "@mui/material/TextField";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { getData, postFetchData } from "../helper/api";
import ReChartClass from "./rechart";
import { useAuthContext } from "../auth/auth-context";
import { useParams } from "react-router-dom";
import useIsMobile from "./mobileview";
import ConfirmDialog from "./confirmation";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useLoading } from "../hooks/loadingcontext";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import loadingAnimation from "../assets/loadingAnimation.gif";
import MessageActions from "./messageActions";
import ChatMarkdown from "./chatmarkdown";
import { appInsights } from "../providers/app-insight-provider";
import LinkLogo from "../assets/linklogoblack.svg";

const FooAvatar = (showVerified = false) => (
  <StyledBadge
    overlap="circular"
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
    badgeContent={
      showVerified ? (
        <Tooltip title="Grounded in Data">
          <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
        </Tooltip>
      ) : null
    }
  >
    <Avatar
      sx={{
        width: 22,
        height: 22,
      }}
      src={avatarImg1}
    />
  </StyledBadge>
);

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#fff",
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.background.paper}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
}));

const suggestionsList = [
  "Give me a list of metrics I can ask about.",
  "What tools are available that I can use?",
  "Which dashboard should I use for leasing metrics?",
  "What is the overall deal count for deals in the East region between Jan 2025 to June 2025?",
  "What is the ssnoi growth for the East region in June 2025?",
];

const MainPage = () => {
  const [inputMessage, setInputMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const bottomOfMessagesRef = useRef(null);
  const { state } = useAuthContext();
  const { id } = useParams();
  const isMobile = useIsMobile();
  const inputRef = useRef(null);
  const [openClearChatConfirmation, setOpenClearChatConfirmation] =
    useState<boolean>(false);

  const [onLoadComplete, setOnLoadComplete] = useState(false);
  const { isLoading, setIsLoading } = useLoading();
  const [loaderText, setLoaderText] = useState("Thinking");

  const InsideLiContext = createContext(false);

  const UserAvatar = () => (
    <StyledBadge
      overlap="circular"
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Avatar src={state.user.profilePicURL} />
    </StyledBadge>
  );

  useEffect(() => {
    const handleLoad = async () => {
      var cachedMessages = await getData(`chat?worksheetId=${id}`);
      setOnLoadComplete(true);
      setMessages([]);
      if (cachedMessages !== null && cachedMessages.length > 0) {
        cachedMessages.forEach((element) => {
          setMessages((prevItems) => [
            ...prevItems,
            {
              sender: element.role === "user" ? "user" : "bot",
              text:
                element.role === "user"
                  ? element.requestContent
                  : element.message,
              data: element,
            },
          ]);
        });
      }
      handleRefocus();
    };
    handleLoad();
  }, [id]);

  useEffect(() => {
    if (!onLoadComplete) {
      return;
    }
    // Scroll to the bottom after the component mounts
    bottomOfMessagesRef.current.scrollTo(
      0,
      bottomOfMessagesRef.current.scrollHeight,
    );
    handleRefocus();
  }, [messages]);

  const handleChunk = (delta: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];

      return [...prev.slice(0, -1), { ...last, text: last.text + delta }];
    });
  };

  const handleResponseData = (data) => {
    // Case 1: response.output_item.done && function_call
    if (
      data.type === "response.output_item.done" &&
      data.item.type === "function_call"
    ) {
      // setLoaderText(data.item.name);
      setLoaderText("Analyzing data");
    }

    // Case 2: response.output_item.done && function_call_output
    else if (
      data.type === "response.output_item.done" &&
      data.item.type === "function_call_output"
    ) {
      setLoaderText("Getting output");
    }
    // Case 3: response.output_text.delta
    else if (data.type === "response.output_text.delta") {
      handleChunk(data.delta);
      setLoaderText("Loading data");
    }
    // Case 4: custom_outputs
    else if (data.message !== null) {
      setTimeout(() => {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1].text = data.message;
          updated[updated.length - 1].data = data;
          return updated;
        });
      }, 100);
    }
  };

  const startStream = async (inputText) => {
    try {
      if (inputText === null || inputText === "") {
        return;
      }
      setMessages((prevItems) => [
        ...prevItems,
        { sender: "user", text: inputText },
      ]);

      var requestModel = {
        content: inputText,
        worksheetId: id,
      };

      setIsLoading(true);
      setLoaderText("Thinking");
      setInputMessage("");

      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "", streaming: true },
      ]);

      const response = await postFetchData(`chat/stream`, requestModel);

      if (!response.body) {
        console.error("No response body");
        return;
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const decodedValue = decoder.decode(value, { stream: true });
        buffer += decodedValue;

        // Process complete SSE events (separated by \n\n)
        let boundary;
        while ((boundary = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, boundary).trim();
          buffer = buffer.slice(boundary + 2);

          if (rawEvent.startsWith("data:")) {
            // Combine multiple data: lines
            const data = rawEvent
              .split("\n")
              .map((line) => line.replace(/^data:\s?/, ""))
              .join("\n");

            if (data === "[DONE]") {
              setTimeout(() => {
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1].streaming = false; // ✅ stop dots
                  return updated;
                });
              }, 300);

              setIsLoading(false);
              return;
            }

            try {
              const parsed = JSON.parse(data);
              handleResponseData(parsed);
            } catch (error) {
              resetOnError();
              console.error("Error: ", error);
              appInsights.trackException({
                exception: error,
                properties: {
                  component: "MainPage",
                  method: "startStreamWhile",
                  inputMessage: inputText,
                  infoMessage: buffer,
                  fullDatabricksChunk: decodedValue,
                },
              });
              continue;
            }
          }
        }
      }
    } catch (error) {
      resetOnError();
      console.error("Error: ", error);
      appInsights.trackException({
        exception: error,
        properties: {
          component: "MainPage",
          method: "startStream",
          inputMessage: inputText,
        },
      });

      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1].text =
          "I'm experiencing a delay accessing the database. Please retry your query or try rephrasing your question.";
        updated[updated.length - 1].streaming = false; // ✅ stop dots
        return updated;
      });
    }
  };

  const resetOnError = () => {
    setIsLoading(false);
    setLoaderText("Thinking");
  };

  const onClearChat = async () => {
    setMessages([]);

    setIsLoading(true);

    await getData(`chat/clearchat?worksheetId=${id}`);

    setIsLoading(false);
  };

  const handleRefocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const LiWrapper: React.FC<React.HTMLAttributes<HTMLLIElement>> = ({
    children,
    ...props
  }) => {
    return (
      <li {...props}>
        <InsideLiContext.Provider value={true}>
          {children}
        </InsideLiContext.Provider>
      </li>
    );
  };

  const PWrapper: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({
    children,
    ...props
  }) => {
    const insideLi = useContext(InsideLiContext);

    if (insideLi) {
      // Avoid wrapping <p> inside <li>
      return <>{children}</>;
    }

    return (
      <p style={{ marginBottom: "1em" }} {...props}>
        {children}
      </p>
    );
  };

  return (
    <>
      {onLoadComplete && (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: messages.length > 0 ? 10 : "45%",
            transform:
              messages.length > 0 ? "translate(-50%, 0)" : "translate(-50%, 0)",
          }}
          width={"100%"}
          key={"container"}
        >
          <Box
            ref={bottomOfMessagesRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: messages.length > 0 ? "calc(100vh - 180px)" : "300px",
              overflow: "auto",
              marginBottom: "10px",
              scrollbarWidth: "thin",
              paddingRight: isMobile ? "5%" : "22%",
              paddingLeft: isMobile ? "5%" : "22%",
            }}
            key={"chatsection"}
          >
            {messages.map((item, index) => (
              <>
                {item.sender === "bot" ? (
                  <>
                    {/* Header */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {FooAvatar(
                        item.data &&
                          item.data.dataModel &&
                          item.data.dataModel.length > 0,
                      )}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>
                        Orion
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-start"
                      gap={1}
                    >
                      <span
                        style={{
                          padding: "10px",
                          borderRadius: "20px",
                          marginLeft: "24px",
                          listStylePosition: "inside",
                        }}
                      >
                        <div className="markdown-container">
                          <ChatMarkdown content={item.text} />
                        </div>
                        {item.role === "assistant" && item.streaming && (
                          <div className="typing">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        )}
                      </span>
                    </Stack>
                    <MessageActions responseData={item.data} worksheetId={id} />
                    {item.data &&
                      item.data.dataModel &&
                      item.data.dataModel.length > 0 && (
                        <>
                          <Box
                            sx={{
                              marginRight: "10px",
                              marginTop: "10px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "stretch",
                            }}
                          >
                            <ReChartClass
                              apiData={item.data}
                              index={index}
                            ></ReChartClass>
                          </Box>
                        </>
                      )}
                  </>
                ) : (
                  <div id={index + "messageuser"} className="messageuser">
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      gap={1}
                    >
                      <span
                        style={{
                          backgroundColor: "#E8EFF0",
                          padding: "10px",
                          borderRadius: "20px",
                          marginRight: "5px",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {item.text}
                      </span>
                      <UserAvatar />
                    </Stack>
                    <MessageActions responseData={item.data} worksheetId={id} />
                  </div>
                )}
              </>
            ))}
          </Box>
          <Box
            sx={{
              marginTop: "auto",
              width: isMobile ? "90%" : "70%",
              marginLeft: isMobile ? "5%" : "15%",
            }}
            component="footer"
            key={"footer"}
          >
            <>
              {messages.length < 1 && (
                <>
                  <Typography
                    variant="h4"
                    noWrap
                    component="div"
                    color="#252429"
                    fontWeight={"bold"}
                    marginBottom={"8px"}
                    marginLeft={isMobile ? "18%" : "12%"}
                  >
                    What can I<div>help you with,</div>
                    <div>
                      {state.user ? "" + state.user.name.split(" ")[0] : ""}?
                    </div>
                  </Typography>
                </>
              )}
              {/* footer */}
              <div id="footertop">
                {isLoading && (
                  <>
                    <span id="loader" className="loader">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center", // vertically align caption + gif
                        }}
                      >
                        {loaderText}

                        {/* Cropped GIF */}
                        <div className="loaderImageCrop">
                          <img
                            src={loadingAnimation}
                            alt="loading..."
                            className="loaderImage"
                          />
                        </div>
                      </div>
                    </span>
                  </>
                )}
              </div>

              <Box className="chatbar">
                <Box className="chatbar-inner">
                  <TextField
                    minRows={messages.length < 1 ? 5 : 1}
                    maxRows={8}
                    inputRef={inputRef}
                    id="outlined-multiline-flexible"
                    fullWidth
                    variant="standard"
                    size="medium"
                    autoComplete="off"
                    placeholder="Ask your question..."
                    InputProps={{
                      disableUnderline: true,
                    }}
                    onChange={(event) => {
                      setInputMessage(event.target.value);
                    }}
                    value={inputMessage}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" && !ev.shiftKey) {
                        startStream(inputMessage);
                        ev.preventDefault();
                      }
                    }}
                    multiline
                    disabled={isLoading}
                  />
                  <Box className="chatbar-actions">
                    <Button
                      variant="outlined"
                      sx={{
                        borderRadius: "20px",
                        color: "black",
                        borderColor: "black",
                        float: "right",
                        marginTop: "8px",
                        textTransform: "none",
                        maxHeight: "26px",
                      }}
                      onClick={() => setOpenClearChatConfirmation(true)}
                      disabled={isLoading}
                      size="small"
                    >
                      Clear Chat
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderRadius: "20px",
                        color: "white",
                        marginTop: "8px",
                        textTransform: "none",
                        maxHeight: "26px",
                        backgroundColor: "black",
                        "&.Mui-disabled": {
                          backgroundColor: "black",
                          color: "white",
                          opacity: 0.6, // optional: keep disabled look
                        },
                      }}
                      endIcon={<RocketLaunchOutlinedIcon />}
                      onClick={() => startStream(inputMessage)}
                      disabled={isLoading}
                      size="small"
                    >
                      Submit
                    </Button>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  paddingLeft: "10%",
                }}
              >
                {messages.length < 1 && (
                  <ul className="suggest-list">
                    {suggestionsList.map((item, index) => (
                      <li
                        key={item}
                        onClick={() => {
                          setInputMessage(item);
                          startStream(item);
                        }}
                      >
                        <TrendingUpIcon
                          fontSize="small"
                          sx={{ marginRight: "4px", paddingTop: "4px" }}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </Box>
            </>
          </Box>
        </Box>
      )}

      {!isMobile && (
        <Box
          component="img"
          src={LinkLogo}
          sx={{
            position: "absolute",
            bottom: 24,
            left: 20,
          }}
        />
      )}

      <ConfirmDialog
        title="Confirmation?"
        open={openClearChatConfirmation}
        handleClose={() => setOpenClearChatConfirmation(false)}
        onConfirm={() => onClearChat()}
        key={"clearchatconfirmation"}
      >
        Are you sure you want to clear chat?
      </ConfirmDialog>
    </>
  );
};

export default MainPage;
